import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useSavePortfolioBridge } from '@/hooks/useSavePortfolioBridge';
import { usePortfolioBridgeStore } from '@/pages/OilAndGasMap/PortfolioBridge/portfolioBridgeStore';
import { MaAssetEntityTypeEnum } from '@/types/GraphqlSchema';
import { PORTFOLIO_ANALYSIS_PATH } from '@/utils/constants';

const portfolioBridgePanelStyles = {
  panel: {
    position: 'absolute',
    top: '5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    bgcolor: 'rgba(33, 33, 33, 0.95)',
    color: 'white',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 2,
    py: 1,
    minWidth: '500px',
  },
  statsText: {
    fontWeight: 600,
    letterSpacing: '0.5px',
    fontSize: '13px',
  },
  spacer: {
    flex: 1,
  },
  actionButton: {
    color: '#42a5f5',
    textTransform: 'uppercase',
    fontSize: '12px',
    fontWeight: 600,
    '&:hover': {
      bgcolor: 'rgba(66, 165, 245, 0.1)',
    },
  },
  closeIcon: {
    cursor: 'pointer',
    fontSize: '20px',
    '&:hover': {
      opacity: 0.7,
    },
  },
};

const PortfolioBridgePanelComponent = () => {
  const { stats, entities, filters, clearSelection } = usePortfolioBridgeStore(state => ({
    stats: state.stats,
    entities: state.entities,
    filters: state.filters,
    clearSelection: state.clearSelection,
  }));

  const { savePortfolioBridge } = useSavePortfolioBridge();
  const history = useHistory();

  const onClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const handleViewInPA = useCallback(async () => {
    const assetsAndBlocks = entities
      .filter(e => e.type === MaAssetEntityTypeEnum.Asset || e.type === MaAssetEntityTypeEnum.Block)
      .map(entity => ({
        legacyId: entity.legacyId,
        country: {
          __typename: 'Country',
          id: entity.country.id,
          isoCode: entity.country.isoCode,
        },
        displayName: entity.name,
        name: entity.name,
        menuName: entity.name,
        __typename: entity.type === MaAssetEntityTypeEnum.Asset ? 'Asset' : 'Block',
      }));

    const uuid = await savePortfolioBridge({
      assets: assetsAndBlocks,
      countries: filters.countries,
      companies: filters.companies,
      basins: filters.basins,
      shoreStatuses: filters.shoreStatuses,
      developmentStatuses: filters.developmentStatuses,
      npvMin: filters.npvMin,
      npvMax: filters.npvMax,
      productionMin: filters.productionMin,
      productionMax: filters.productionMax,
      reservesMin: filters.reservesMin,
      reservesMax: filters.reservesMax,
      operator: filters.operator,
      opportunities: filters.opportunities,
    });

    if (uuid) {
      history.push(`${PORTFOLIO_ANALYSIS_PATH}?uuid=${uuid}`);
    }
  }, [entities, filters, savePortfolioBridge, history]);

  const statsText = [
    stats.totalAssets > 0 && `${stats.totalAssets} ASSET${stats.totalAssets > 1 ? 'S' : ''}`,
    stats.totalBlocks > 0 && `${stats.totalBlocks} BLOCK${stats.totalBlocks > 1 ? 'S' : ''}`,
    stats.totalFields > 0 && `${stats.totalFields} FIELD${stats.totalFields > 1 ? 'S' : ''}`,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <Paper elevation={3} data-id='portfolio-bridge-panel' sx={portfolioBridgePanelStyles.panel}>
      <Typography
        variant='body2'
        data-id='portfolio-bridge-stats-text'
        sx={portfolioBridgePanelStyles.statsText}
      >
        {statsText}
      </Typography>

      <Box sx={portfolioBridgePanelStyles.spacer} />

      <Button
        variant='text'
        onClick={handleViewInPA}
        data-id='portfolio-bridge-view-pa-button'
        sx={portfolioBridgePanelStyles.actionButton}
      >
        View assets in PA tool
      </Button>

      <CloseIcon
        onClick={onClose}
        data-id='portfolio-bridge-close-button'
        sx={portfolioBridgePanelStyles.closeIcon}
      />
    </Paper>
  );
};

export const PortfolioBridgePanel = () => {
  const { stats } = usePortfolioBridgeStore(state => ({
    stats: state.stats,
  }));

  if (stats.total === 0) return null;

  return createPortal(<PortfolioBridgePanelComponent />, document.body);
};
